import { Injectable } from "@angular/core"
import { AngularFirestoreCollection } from "@angular/fire/firestore"
import { CustomFields, RunTemplate, SelectedOperatingSystem, TestRun } from "@tr/models"
import { TrDatePipe } from "@tr/pipes"
import * as firebase from "firebase/app"
import { DateTime } from "luxon"
import * as moment from "moment"
import { FirebaseCollectionBase } from "./firebase-resource-base"
import { FirebaseService } from "./firebase.service"

@Injectable({
  providedIn: "root",
})
export class RunTemplatesService extends FirebaseCollectionBase<RunTemplate> {
  public initialized = false
  public getId = (item: RunTemplate) => item._id

  constructor(fb: FirebaseService) {
    super(fb)
  }

  public getCollection(company: string, project: string): AngularFirestoreCollection {
    return this.firebaseService
      .collection("companies")
      .doc(company)
      .collection("projects")
      .doc(project)
      .collection<RunTemplate>("runTemplates")
  }

  public async getData(company, project): Promise<RunTemplate[]> {
    const collectionSnapshot = await this.getCollection(company, project)
      .ref.orderBy("startTime")
      .get()

    this.data = collectionSnapshot.empty
      ? []
      : collectionSnapshot.docs.map<RunTemplate>(runTemplateSnapshot => {
        const runTemplate = runTemplateSnapshot.data() as RunTemplate
        runTemplate._id = runTemplateSnapshot.id
        this.dataMap[runTemplateSnapshot.id] = runTemplate
        return runTemplate
      })

    this.initialized = true
    return this.data
  }

  public async getDataItem(runTemplateId: string, company: string, project: string, refresh = false): Promise<RunTemplate> {
    if (refresh || !this.dataMap[runTemplateId]) {
      const itemSnapshot = await this.getCollection(company, project)
        .doc(runTemplateId)
        .ref.get()
      const item = itemSnapshot.data() as RunTemplate
      item._id = itemSnapshot.id
      this.dataMap[runTemplateId] = item
    }

    return this.dataMap[runTemplateId]
  }

  public async delete(company: string, project: string, id: string): Promise<void> {
    await this.getCollection(company, project)
      .doc(id)
      .delete()
  }

  public fromTestRun(
    testRun: TestRun,
  ): RunTemplate {
    return {
      timezone: testRun.timezone || DateTime.local().zoneName,
      project: testRun.project,
      runTitle: testRun.runTitle || null,
      runSprintNumber: testRun.runSprintNumber,
      internal: testRun.internal,
      runPace: testRun.runPace,
      customFields: this.toServerCustomFields(testRun.customFields || {}),
      schedule: testRun.schedule,
      totalSteps: testRun.totalSteps,
      totalTests: testRun.totalTests,
      completionDays: testRun.completionDays,
      includeWeekends: testRun.includeWeekends,
      totalPlatforms: testRun.totalPlatforms,
      totalDevices: testRun.totalDevices,
      totalEmulators: testRun.totalEmulators,
      totalCredits: testRun.totalCredits,
      selectedEnvironments: testRun.selectedEnvironments,
      selectedRunPlatforms: testRun.selectedRunPlatforms,
      selectedDevices: testRun.selectedDevices,
      selectedEmulators: testRun.selectedEmulators,
      selectedTestTypes: testRun.selectedTestTypes,
      selectedTestStatus: testRun.selectedTestStatus,
      selectedFeatures: testRun.selectedFeatures,
      selectedTags: testRun.selectedTags,
      selectedPlatforms: testRun.selectedPlatforms,
      selectedAuthors: testRun.selectedAuthors,
      isSelectedEnvironments: testRun.isSelectedEnvironments,
      isEnvironmentDefaults: testRun.isEnvironmentDefaults,
      loginTokens: testRun.loginTokens,
      isMobileRun: testRun.isMobileRun,
      bugSeverityCount: testRun.bugSeverityCount,
      dueDate: testRun.dueDate,
      startTime: testRun.startTime,
      completionPercentage: testRun.completionPercentage,
      passed: 0,
      failed: 0,
      incomplete: testRun.totalTests,
      blocked: 0,
      id: testRun.id,
      _id: "",
      selectedOsVersionsBrowsers: testRun.selectedOsVersionsBrowsers,
      selectedTests: testRun.selectedTests,
      templateId: "",
      runId: -1,
      notes: [],
      selectedOsVersionsEmulators: testRun.selectedOsVersionsEmulators,
      selectedOsVersionsDevices: testRun.selectedOsVersionsDevices,
      totalOsVersion: 0,
      selectedOperatingSystems: testRun.selectedOperatingSystems,
    }
  }

  public toServerCustomFields(customFields: CustomFields): CustomFields {
    Object.keys(customFields).forEach(key => {
      if (customFields[key].type === "date") {
        if (customFields[key].value) {
          const newDate = TrDatePipe.toDate(customFields[key].value)
          customFields[key].value = moment(newDate).isValid() ? firebase.firestore.Timestamp.fromDate(newDate) as any : null
        }
      }
    })

    return customFields
  }

  public toTestRun(
    template: RunTemplate,
    selectedBrowsers: string[],
    selectedOperatingSystems: SelectedOperatingSystem[],
    selectedTests: string[],
    loginTokens: string[],
    parentId: string,
    startTime: Date,
    dueDate: Date,
    company: string,
    uid: string,
    bugSeverityCount: any[],
  ): TestRun {
    return {
      internal: template.internal,
      _id: null,
      timezone: template.timezone || DateTime.local().zoneName,
      schedule: template.schedule,
      customFields: this.toServerCustomFields(template.customFields || {}),
      runTitle: template.runTitle || null,
      runSprintNumber: template.runSprintNumber,
      runPace: template.runPace,
      selectedEnvironments: template.selectedEnvironments,
      selectedRunPlatforms: template.selectedRunPlatforms,
      selectedDevices: template.selectedDevices,
      selectedEmulators: template.selectedEmulators,
      selectedFeatures: template.selectedFeatures,
      selectedTags: template.selectedTags,
      selectedPlatforms: template.selectedPlatforms,
      selectedAuthors: template.selectedAuthors,
      selectedTestTypes: template.selectedTestTypes,
      selectedTestStatus: template.selectedTestStatus,
      selectedOsVersionsDevices: template.selectedOsVersionsDevices,
      selectedOsVersionsEmulators: template.selectedOsVersionsEmulators,
      selectedOsVersionsBrowsers: selectedBrowsers,
      selectedOperatingSystems,
      isSelectedEnvironments: template.isSelectedEnvironments,
      selectedTests,
      isMobileRun: template.isMobileRun,
      isEnvironmentDefaults: template.isEnvironmentDefaults,
      totalSteps: template.totalSteps,
      totalCredits: template.totalCredits,
      totalTests: template.totalTests,
      totalPlatforms: template.totalPlatforms,
      totalDevices: template.totalDevices,
      totalEmulators: template.totalEmulators,
      totalOsVersion: template.totalOsVersion,
      completionDays: template.completionDays,
      includeWeekends: template.includeWeekends,
      loginTokens,
      project: template.project,
      parentId,
      dueDate: firebase.firestore.Timestamp.fromDate(dueDate),
      startTime: firebase.firestore.Timestamp.fromDate(startTime),
      company,
      completionPercentage: 0,
      passed: template.passed,
      failed: template.failed,
      incomplete: template.incomplete,
      blocked: template.blocked,
      id: "",
      uid,
      runId: -1,
      bugSeverityCount,
      notes: template.notes,
      dateModified: undefined,
      scheduleFailed: false,
      scheduleFailedInfo: undefined,
    }
  }

  public generateNewDataItem(project: string): RunTemplate {
    return {
      timezone: DateTime.local().zoneName,
      project,
      runTitle: "",
      runSprintNumber: "",
      customFields: {},
      schedule: null,
      internal: false,
      runPace: 1,
      totalSteps: 0,
      totalTests: 0,
      completionDays: 0,
      includeWeekends: false,
      totalPlatforms: 0,
      totalDevices: 0,
      totalEmulators: 0,
      totalCredits: 0,
      selectedEnvironments: [],
      selectedRunPlatforms: [],
      selectedDevices: [],
      selectedEmulators: [],
      selectedTestTypes: [],
      selectedTestStatus: [],
      selectedFeatures: [],
      selectedTags: [],
      selectedPlatforms: [],
      selectedAuthors: [],
      isSelectedEnvironments: false,
      isEnvironmentDefaults: false,
      loginTokens: [],
      isMobileRun: false,
      bugSeverityCount: [],
      dueDate: new Date(),
      startTime: new Date(),
      completionPercentage: 0,
      passed: 0,
      failed: 0,
      incomplete: 0,
      blocked: 0,
      id: "",
      _id: "",
      selectedOsVersionsBrowsers: [],
      selectedTests: [],
      templateId: "",
      runId: -1,
      notes: [],
      selectedOsVersionsEmulators: [],
      selectedOsVersionsDevices: [],
      totalOsVersion: 0,
      selectedOperatingSystems: [],
    }
  }

  public updateWithDefaults(template: RunTemplate): RunTemplate {
    const defaultTemplate = this.generateNewDataItem(template.project)

    Object.keys(defaultTemplate).forEach(key => {
      template[key] = template[key] || defaultTemplate[key]
    })

    return template
  }

  public async update(
    company: string,
    project: string,
    runTemplateId: string,
    runTemplateToUpdate: any,
  ): Promise<void> {
    return this.getCollection(company, project)
      .doc(runTemplateId)
      .update(runTemplateToUpdate)
  }
}
